import { useContext, useEffect, useState } from "react";
import { UserContext } from "../../../../storage/contexts/UserContext";
import FadeIn from "react-fade-in";
import axios from "axios";
import {
  DOWNLOAD_QUESTIONNAIRE_CSV,
  GET_QUESTIONNAIRE_RESPONSES,
} from "../../../../Routes";
import { generateURLOptions } from "../../../../URLOptions";
import { useParams } from "react-router";
import { FaChevronLeft } from "react-icons/fa6";

import moodSad from "../../../../assets/images/moods/ic_sad.png";
import moodUnhappy from "../../../../assets/images/moods/ic_unhappy.png";
import moodOkay from "../../../../assets/images/moods/ic_okay.png";
import moodHappy from "../../../../assets/images/moods/ic_happy.png";
import moodElated from "../../../../assets/images/moods/ic_elated.png";
import FilledButton from "../../../../InterstellrComponents/FilledButton";

const OrganisationQuestionnaireResponses = () => {
  const { id } = useParams();
  const { profile } = useContext(UserContext);

  const [questionnaire, setQuestionnaire] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  const getQuestionnaireResponses = async () => {
    try {
      const response = await axios.get(GET_QUESTIONNAIRE_RESPONSES + id, {
        headers: generateURLOptions(),
      });

      const data = response.data.data;
      setQuestionnaire(data.questionnaire);
      setQuestions(data.responses); // each item = question + all answers
    } catch (error) {
      alert(error);
    }
  };

  const downloadCSV = async () => {
    try {
      const response = await axios.post(
        DOWNLOAD_QUESTIONNAIRE_CSV,
        { questionnaireId: id },
        {
          headers: generateURLOptions(),
          responseType: "blob", // IMPORTANT
        }
      );

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `questionnaire_${id}.csv`;
      link.click();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(error);
    }
  };

  useEffect(() => {
    getQuestionnaireResponses();
  }, []);

  const moodMap = {
    0: { icon: moodSad, label: "Sad" },
    1: { icon: moodUnhappy, label: "Unhappy" },
    2: { icon: moodOkay, label: "Okay" },
    3: { icon: moodHappy, label: "Happy" },
    4: { icon: moodElated, label: "Elated" },
  };

  return (
    <div
      className="w-screen h-[90vh] overflow-hidden"
      style={{
        background: `linear-gradient(45deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.8) 45%, ${
          profile?.company_id?.company_primary_color ?? "rgba(81,83,245,1)"
        } 150%)`,
      }}
    >
      <div className="container mx-auto flex flex-col md:flex-row mt-24 h-full gap-6 px-4">
        {/* LEFT PANEL — QUESTIONS */}
        <div className="w-full md:w-5/12 overflow-y-auto p-6 h-[75vh]">
          <FadeIn delay={200}>
            <div
              onClick={() => window.history.back()}
              className="flex flex-row justify-start items-center cursor-pointer mb-1"
            >
              <FaChevronLeft size={16} />
              <p className="text-base font-medium ml-2">Back</p>
            </div>

            <h4 className="text-xl font-semibold text-black mb-1">
              Questionnaire Responses
            </h4>

            <h5 className="text-3xl font-bold text-black mb-1">
              {questionnaire?.name}
            </h5>

            <h5 className="text-lg font-medium text-purple-600 mb-2">
              {questions.length} Questions
            </h5>

            <FilledButton
              marginTop={0}
              marginBottom={12}
              onPress={downloadCSV}
              text={"Export Questionnaire Data"}
            />

            <h4 className="text-slate-500 font-medium mb-3">
              Select a Question
            </h4>

            <div className="flex flex-col gap-4">
              {questions.map((q, idx) => (
                <button
                  key={q._id}
                  onClick={() => setSelectedQuestion(q)}
                  className={`rounded-lg text-left p-4 border transition ${
                    selectedQuestion?._id === q._id
                      ? "bg-blue-100 border-blue-500"
                      : "bg-slate-100 border-slate-200"
                  }`}
                >
                  <h5 className="font-semibold text-black mb-1">
                    {idx + 1}. {q.title}
                  </h5>

                  <p className="text-slate-500 text-sm">
                    {q.answers.length} responses
                  </p>
                </button>
              ))}
            </div>
          </FadeIn>
        </div>

        {/* RIGHT PANEL — RESPONSES FOR SELECTED QUESTION */}
        <div className="w-full md:w-7/12 bg-white rounded-xl overflow-y-auto p-6 h-[75vh]">
          {!selectedQuestion ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-500 text-center">
              <h4 className="text-2xl font-bold mt-6">
                Select a question to view its responses
              </h4>
            </div>
          ) : (
            <FadeIn delay={200}>
              <h4 className="text-2xl font-bold mb-4">
                {selectedQuestion.title}
              </h4>

              {/* RENDER RESPONSES BY QUESTION TYPE */}
              {selectedQuestion.answers.map((ans, index) => (
                <div key={ans._id} className="mb-6">
                  {/* TYPE 0 — Mood */}
                  {selectedQuestion.type === 0 && (
                    <div className="flex items-center">
                      <img
                        className="w-12 h-12"
                        src={moodMap[ans.mood]?.icon}
                        alt="mood"
                      />
                      <span className="ml-3 text-lg capitalize">
                        {moodMap[ans.mood]?.label}
                      </span>
                    </div>
                  )}

                  {/* TYPE 1 — Single choice */}
                  {selectedQuestion.type === 1 &&
                    (ans.options?.[0]?.optionId?.title ? (
                      <p className="text-black text-lg">
                        {ans.options[0].optionId.title}
                      </p>
                    ) : (
                      <p className="text-slate-500 italic">No response</p>
                    ))}

                  {/* TYPE 2 — Multi-choice */}
                  {selectedQuestion.type === 2 && (
                    <ul className="list-disc ml-6">
                      {ans.options.map((opt, oIdx) => (
                        <li key={oIdx} className="text-black text-lg">
                          {opt.optionId.title}
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* TYPE 3 — Long Text */}
                  {selectedQuestion.type === 3 && (
                    <p className="text-black text-lg">
                      {ans.responseText || (
                        <span className="text-slate-500 italic">
                          No written response
                        </span>
                      )}
                    </p>
                  )}

                  <hr className="border-slate-200 mt-4" />
                </div>
              ))}
            </FadeIn>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganisationQuestionnaireResponses;
